import { useCurrentUser } from "hooks/useCurrentUser";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { useGetAccountRevenueSummaryQuery } from "store/queries/account";
import styled from "styled-components";

const CardStyled = styled.div`
  padding: 0.5rem 0.75rem;

  border: 1px solid var(--brand-gray-200);
  border-radius: 14px;
  background-color: var(--brand-white);
`;

const TitleStyled = styled.div`
  color: var(--brand-black);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-semibold);
  line-height: 1.25;
`;

const SubtitleStyled = styled.div`
  color: var(--brand-gray-600);
  font-size: 0.75rem;
  line-height: 1.25;
`;

// Matches the header's Direct Pay button, which is a styled(Button) carrying the
// same gap between icon and label.
const SeeMoreButtonStyled = styled(Button)`
  gap: ${sizer(2)};
`;

// The track holds both figures, so it has to be tall enough for a label above an
// amount. The fill is a bar *behind* that text, not a container for it.
const TrackStyled = styled.div`
  position: relative;
  width: 100%;

  padding: 0.3125rem 0.875rem;

  border: 1px solid var(--brand-gray-200);
  border-radius: 10px;
  background-color: var(--brand-gray-50);
  overflow: hidden;

  /* Only once the card is a row does flex-basis mean width. Setting it
     unconditionally made the basis a *height* while stacked, which is what blew
     the bar up to 20rem tall on a phone. Matches the card's lg:flex-row. */
  @media (min-width: 992px) {
    flex: 1 1 20rem;
    min-width: 16rem;
    width: auto;
  }
`;

const FillStyled = styled.div<{ $percent: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $percent }) => $percent}%;

  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--brand-purple-700) 14%, var(--brand-white)) 0%,
    var(--brand-blue-25) 100%
  );
`;

// Marks where the fill ends, so the eye can find the boundary even where the
// gradient has faded out to nearly white.
const MarkerStyled = styled.div<{ $percent: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $percent }) => $percent}%;

  border-left: 1px solid var(--brand-gray-400);
`;

const PercentPillStyled = styled.span<{ $percent: number }>`
  position: absolute;
  top: 50%;
  left: ${({ $percent }) => $percent}%;
  transform: translate(-50%, -50%);

  padding: 0.125rem 0.5rem;

  border-radius: 999px;
  /* Same recipe as StatusBadge's "positive" tone, so the greens agree. */
  background-color: color-mix(
    in srgb,
    var(--chip-text-success) 14%,
    var(--brand-white)
  );

  color: var(--chip-text-success);
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
`;

const FigureLabelStyled = styled.div`
  font-size: 0.625rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
  line-height: 1.3;
  text-transform: uppercase;
`;

const FigureAmountStyled = styled.div`
  font-size: 1.0625rem;
  font-weight: var(--font-weight-semibold);
  line-height: 1.2;
`;

const CollectedStyled = styled.div`
  position: relative;
  color: var(--brand-purple-700);
`;

const ExpectedStyled = styled.div`
  position: relative;
  text-align: right;
  color: var(--brand-black);

  ${FigureLabelStyled} {
    color: var(--brand-gray-600);
  }
`;

// Whole dollars, as in the design — parsePrice always renders cents, which is too
// much precision for a figure this size.
const formatWholeDollars = (amountInCents: number) =>
  (amountInCents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export function MonthlyCollections({ accountId }: { accountId?: string }) {
  const navigate = useNavigate();
  const { userInfo } = useCurrentUser({ skip: false });
  const { data, isLoading, isError } = useGetAccountRevenueSummaryQuery(
    accountId as string,
    { skip: !accountId }
  );

  const summary = data?.data;

  // Nothing to show is better than a card full of zeros that looks like a real
  // reading of the month.
  if (!accountId || isLoading || isError || !summary) {
    return null;
  }

  // Served by the API, which is null when nothing is expected: there is no rate to
  // show, which is not the same as having collected none of what was due.
  const { conversionRatePercent } = summary;

  // Collecting more than expected is possible (an over-payment, or a plan paid
  // ahead), and the bar cannot render past its own end.
  const fillPercent = Math.max(0, Math.min(100, conversionRatePercent ?? 0));

  return (
    <CardStyled
      className="flex flex-column lg:flex-row lg:align-items-center gap-2 mb-3"
      data-testid="monthly_collections_OrderTable"
    >
      <div className="flex-shrink-0">
        <TitleStyled>Monthly collections</TitleStyled>
        <SubtitleStyled>Current performance</SubtitleStyled>
      </div>

      <TrackStyled className="flex align-items-center justify-content-between gap-2">
        <FillStyled $percent={fillPercent} />

        <CollectedStyled>
          <FigureLabelStyled>Collected</FigureLabelStyled>
          <FigureAmountStyled data-testid="monthly_collections_collected_OrderTable">
            {formatWholeDollars(summary.amountCollectedInCents)}
          </FigureAmountStyled>
        </CollectedStyled>

        {/* No rate to plot when nothing is expected this month. */}
        {conversionRatePercent !== null && (
          <>
            <MarkerStyled $percent={fillPercent} />
            <PercentPillStyled
              $percent={fillPercent}
              data-testid="monthly_collections_percent_OrderTable"
            >
              {Math.round(conversionRatePercent)}%
            </PercentPillStyled>
          </>
        )}

        <ExpectedStyled>
          <FigureLabelStyled>Expected</FigureLabelStyled>
          <FigureAmountStyled data-testid="monthly_collections_expected_OrderTable">
            {formatWholeDollars(summary.amountExpectedInCents)}
          </FigureAmountStyled>
        </ExpectedStyled>
      </TrackStyled>

      {/* Only offered when the user actually has the Analytics tab — without it the
          route falls back to this same tab, so the button would do nothing. */}
      {userInfo?.analyticsTabEnabled && (
        <SeeMoreButtonStyled
          className="p-button-neutral p-button-sm p-button-rounded-md p-button-shadow flex-shrink-0"
          data-testid="monthly_collections_see_more_OrderTable"
          icon="pi pi-arrow-up-right"
          iconPos="right"
          label="See more"
          onClick={() => navigate(`/hospitals/view/${accountId}/analytics`)}
        />
      )}
    </CardStyled>
  );
}
